// CouncilModelPicker.jsx — searchable model + chairman selection for Council v2.
//
// Extracted from the CouncilView god-component. Preserves the existing selection
// semantics (localStorage-backed in the parent) while adding a free-text search
// over the full roster. The search NEVER mutates the selection — it only filters
// which chips are shown — so a selected model stays selected across searches.
//
// The "Add LLM" dropdown reuses the same searchable groups as before.

import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckSquare, Crown, Plus, Search, Square, X } from 'lucide-react';
import { CHAIRMAN_COLOR, CHAIRMAN_GLOW, COUNCIL_AGENTS } from '../../lib/councilAgents.js';
import { filterAvailableModels, groupModelsByProvider } from './councilModel.js';
import ModelStatusDot from '../shared/ModelStatusDot.jsx';

function AgentChip({ agent, selected, onToggle, isChairman, onChairman }) {
  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={() => onToggle(agent.id)}
        aria-pressed={selected}
        aria-label={`${selected ? 'Remover' : 'Adicionar'} ${agent.name} ao conselho`}
        className={
          'group inline-flex min-h-[36px] items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-all duration-200 focus:outline-none lg:min-h-0 lg:px-2.5 lg:py-1 lg:text-[11px] '
          + (selected
            ? 'bg-apolo-surface2 text-apolo-textPrimary shadow-sm'
            : 'border-apolo-borderSubtle bg-transparent text-apolo-textSecondary hover:bg-apolo-surface2/60')
        }
        style={selected ? { borderColor: agent.color, boxShadow: `0 0 0 1px ${agent.color}40, 0 0 12px ${agent.glow}` } : {}}
      >
        <span aria-hidden="true" className="inline-flex h-4 w-4 items-center justify-center rounded-full text-[8px] font-bold text-apolo-bg" style={{ backgroundColor: agent.color }}>
          {agent.short}
        </span>
        <span>{agent.name}</span>
      </button>
      {selected && (
        <button
          type="button"
          onClick={() => onChairman(agent.id)}
          aria-pressed={isChairman}
          aria-label={isChairman ? `${agent.name} é Chairman` : `Designar ${agent.name} como Chairman`}
          title={isChairman ? 'Chairman' : 'Designar como Chairman'}
          className={
            'inline-flex h-9 w-9 items-center justify-center rounded-full border transition lg:h-5 lg:w-5 '
            + (isChairman ? 'border-transparent text-apolo-bg' : 'border-apolo-borderSubtle text-apolo-textTertiary hover:text-apolo-textPrimary')
          }
          style={isChairman ? { backgroundColor: CHAIRMAN_COLOR, boxShadow: `0 0 10px ${CHAIRMAN_GLOW}` } : {}}
        >
          <Crown size={12} className="lg:h-[9px] lg:w-[9px]" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

function SearchableAddDropdown({ open, onOpenChange, disabled, count, available, onPick }) {
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const [search, setSearch] = useState('');
  const [pos, setPos] = useState({ top: 0, bottom: null, left: 0, openUp: false });

  const filtered = useMemo(() => filterAvailableModels(available, search), [available, search]);
  const grouped = useMemo(() => groupModelsByProvider(filtered), [filtered]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    function updatePos() {
      const el = triggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const popoverMaxHeight = 380;
      const spaceBelow = window.innerHeight - r.bottom;
      const spaceAbove = r.top;
      const triggerCenter = (r.top + r.bottom) / 2;
      const openUp = triggerCenter > window.innerHeight * 0.55
        || (spaceBelow < popoverMaxHeight && spaceAbove > spaceBelow);
      setPos({ top: openUp ? null : r.bottom + 6, bottom: openUp ? window.innerHeight - r.top + 6 : null, left: r.left, openUp });
    }
    updatePos();
    window.addEventListener('scroll', updatePos, true);
    window.addEventListener('resize', updatePos);
    return () => {
      window.removeEventListener('scroll', updatePos, true);
      window.removeEventListener('resize', updatePos);
    };
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => onOpenChange(!open)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-apolo-borderSubtle bg-transparent px-2.5 py-1 text-[11px] text-apolo-textTertiary transition hover:border-apolo-accent hover:bg-apolo-surface2/40 hover:text-apolo-textPrimary disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus-visible:border-apolo-accent focus-visible:ring-2 focus-visible:ring-apolo-accent/30"
      >
        <Plus size={11} aria-hidden="true" strokeWidth={2.25} />
        Adicionar LLM
        {count > 0 && <span className="rounded-full bg-apolo-surface2 px-1.5 py-px text-[9px] font-medium text-apolo-textSecondary">{count}</span>}
      </button>

      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {open && (
            <motion.div
              ref={popoverRef}
              role="listbox"
              aria-label="Adicionar LLM ao conselho"
              initial={{ opacity: 0, y: pos.openUp ? 6 : -6, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: pos.openUp ? 6 : -6, scale: 0.97 }}
              transition={{ type: 'spring', stiffness: 380, damping: 32, mass: 0.6 }}
              className="fixed z-[100] flex w-[320px] flex-col overflow-hidden rounded-xl border border-apolo-borderSubtle bg-apolo-surface1 shadow-[0_24px_64px_-12px_rgba(0,0,0,0.6),0_0_0_1px_rgba(255,255,255,0.04)] backdrop-blur-md"
              style={{ transformOrigin: pos.openUp ? 'bottom left' : 'top left', top: pos.top ?? undefined, bottom: pos.bottom ?? undefined, left: pos.left }}
            >
              <div className="flex items-center gap-2 border-b border-apolo-borderSubtle bg-apolo-surface2/40 px-3 py-2">
                <Search size={11} aria-hidden="true" strokeWidth={2.25} className="shrink-0 text-apolo-textTertiary" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar modelo ou provider…"
                  autoFocus
                  className="flex-1 bg-transparent text-[11.5px] text-apolo-textPrimary placeholder:text-apolo-textTertiary focus:outline-none"
                />
                {search && (
                  <button type="button" onClick={() => setSearch('')} aria-label="Limpar busca" className="text-apolo-textTertiary transition hover:text-apolo-textPrimary">
                    <X size={10} aria-hidden="true" />
                  </button>
                )}
              </div>
              <div className="max-h-[320px] overflow-y-auto p-1">
                {grouped.length === 0 ? (
                  <div className="px-3 py-6 text-center text-[11px] text-apolo-textTertiary">
                    {search ? 'Nenhum modelo encontrado pra essa busca.' : 'Todos os modelos já estão no conselho.'}
                  </div>
                ) : (
                  grouped.map(({ provider, models }) => (
                    <div key={provider} className="mb-1 last:mb-0">
                      <div className="sticky top-0 z-[1] flex items-center gap-1.5 bg-apolo-surface1/95 px-2 pb-1 pt-2 text-[9.5px] font-semibold uppercase tracking-[0.08em] text-apolo-textTertiary backdrop-blur-sm">
                        {provider}
                        <span className="rounded-full bg-apolo-surface3 px-1.5 text-[9px] font-medium tabular-nums text-apolo-textSecondary ring-1 ring-inset ring-apolo-borderSubtle">{models.length}</span>
                      </div>
                      {models.map((m) => (
                        <button
                          key={m.id}
                          type="button"
                          onClick={() => { onPick(m.id); onOpenChange(false); setSearch(''); }}
                          className="group/item flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition hover:bg-apolo-surface2"
                        >
                          <ModelStatusDot connected={m.connected} size={6} />
                          <code className="flex-1 truncate font-mono text-[11px] text-apolo-textPrimary">{m.id}</code>
                          {m.source === 'adapter' && (
                            <span className="shrink-0 rounded-sm border border-violet-500/30 bg-violet-500/10 px-1 py-px text-[9px] font-medium uppercase tracking-wider text-violet-300">adapter</span>
                          )}
                        </button>
                      ))}
                    </div>
                  ))
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </>
  );
}

/**
 * Searchable roster. `search` filters the CHIPS only — selection is preserved.
 */
export default function CouncilModelPicker({
  roster,
  selectedIds,
  chairmanId,
  onToggle,
  onChairman,
  onSelectAll,
  onClearAll,
  onRemoveCustom,
  onAddModel,
  availableToAdd,
  disabled,
}) {
  const [query, setQuery] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const filteredRoster = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return roster;
    return roster.filter((a) =>
      (a.name || '').toLowerCase().includes(q)
      || (a.provider || '').toLowerCase().includes(q)
      || (a.id || '').toLowerCase().includes(q),
    );
  }, [roster, query]);
  // Keep selected chips visible even when they don't match the search, so the
  // user never loses sight of (or accidentally drops) an active selection.
  const hiddenSelected = useMemo(
    () => roster.filter((a) => selectedIds.includes(a.id) && !filteredRoster.includes(a)),
    [roster, selectedIds, filteredRoster],
  );
  const chips = useMemo(() => [...filteredRoster, ...hiddenSelected], [filteredRoster, hiddenSelected]);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-xs font-medium text-apolo-textSecondary">
          Conselheiros{' '}
          <span className="text-apolo-textTertiary">· {selectedIds.length} de {roster.length} · clique na coroa pra designar o Chairman</span>
        </span>
        <div className="flex items-center gap-1 text-[10px]">
          <button type="button" onClick={onSelectAll} disabled={selectedIds.length === roster.length || disabled} aria-label="Selecionar todos os conselheiros" className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-apolo-textTertiary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary disabled:cursor-not-allowed disabled:opacity-30">
            <CheckSquare size={10} aria-hidden="true" /> Todos
          </button>
          <span aria-hidden="true" className="text-apolo-textTertiary/40">·</span>
          <button type="button" onClick={onClearAll} disabled={selectedIds.length === 0 || disabled} aria-label="Limpar seleção de conselheiros" className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-apolo-textTertiary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary disabled:cursor-not-allowed disabled:opacity-30">
            <Square size={10} aria-hidden="true" /> Limpar
          </button>
        </div>
      </div>

      <div className="relative">
        <Search size={11} aria-hidden="true" className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-apolo-textTertiary" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={disabled}
          placeholder="Buscar conselheiro por nome, provider ou modelo…"
          aria-label="Buscar conselheiro"
          className="w-full rounded-md border border-apolo-borderSubtle bg-apolo-surface2 py-1.5 pl-7 pr-2 text-[11.5px] text-apolo-textPrimary placeholder:text-apolo-textTertiary focus:border-apolo-accent focus:outline-none disabled:opacity-60"
        />
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {chips.map((a) => {
          const isCustom = !COUNCIL_AGENTS.find((c) => c.id === a.id);
          return (
            <div key={a.id} className="relative">
              <AgentChip agent={a} selected={selectedIds.includes(a.id)} onToggle={onToggle} isChairman={chairmanId === a.id} onChairman={onChairman} />
              {isCustom && (
                <button
                  type="button"
                  onClick={() => onRemoveCustom(a.id)}
                  aria-label={`Remover ${a.name}`}
                  title="Remover (custom)"
                  className="absolute -right-1 -top-1 inline-flex h-4 w-4 items-center justify-center rounded-full border border-apolo-borderSubtle bg-apolo-surface1 text-apolo-textTertiary transition hover:border-apolo-error hover:text-apolo-error"
                >
                  <X size={9} aria-hidden="true" />
                </button>
              )}
            </div>
          );
        })}
        <SearchableAddDropdown
          open={showAdd}
          onOpenChange={setShowAdd}
          disabled={disabled || availableToAdd.length === 0}
          count={availableToAdd.length}
          available={availableToAdd}
          onPick={onAddModel}
        />
      </div>
    </div>
  );
}
